
// TableDoklady.jsx (původně TableArchiv / TableDoklady.txt)

import React, { useMemo, useState, useCallback } from "react";
import { useTable, useSortBy, useGlobalFilter } from "react-table";
import "./TableDoklady.css";
import GlobalFilter from "../_components/GlobalFilter";
import { FaPrint } from "react-icons/fa";
import { FaWrench } from "react-icons/fa";
import { SiMicrosoftexcel } from "react-icons/si";

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import 'dayjs/locale/cs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { authHeader } from '../_helpers';
import _ from "lodash";
import Tooltip from '@mui/material/Tooltip';
import * as XLSX from 'xlsx/xlsx.mjs';

//Aktivace pluginu pro přesné parsování formátu z API
dayjs.extend(customParseFormat);

//Formátování pro zobrazení
const formatApiDate = (value) => {
  if (!value) return '';
  let parsed = dayjs(value, 'YYYY-MM-DD HH:mm', true);
  if (parsed.isValid()) return parsed.format('DD.MM.YYYY HH:mm');
  parsed = dayjs(value, 'YYYY-MM-DD HH:mm', true);
  if (parsed.isValid()) return parsed.format('DD.MM.YYYY HH:mm');
  parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : '';
};

//Timestamp pro rychlé číselné řazení
const asTimestamp = (value) => {
  if (!value) return Number.NEGATIVE_INFINITY;
  let p = dayjs(value, 'YYYY-MM-DD HH:mm:ss.SSS', true);
  if (p.isValid()) return p.valueOf();
  p = dayjs(value, 'YYYY-MM-DD HH:mm:ss', true);
  if (p.isValid()) return p.valueOf();
  p = dayjs(value);
  return p.isValid() ? p.valueOf() : Number.NEGATIVE_INFINITY;
};

//sortType pro datumové sloupce
const dateSort = (rowA, rowB, columnId) => {
  const a = asTimestamp(rowA.values[columnId]);
  const b = asTimestamp(rowB.values[columnId]);
  return a === b ? 0 : a > b ? 1 : -1;
};

// Datum pro API query (jen den, bez času)
const toApiDateOnly = (d) => {
  if (!d) return null;
  const parsed = dayjs(d);
  return parsed.isValid() ? parsed.format('YYYY/MM/DD') : null;
};

const TableArchiv = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [datumOd, setDatumOd] = useState();
  const [datumDo, setDatumDo] = useState();
  const user = JSON.parse(localStorage.getItem('user'));

  //Definice sloupců 
  const columns = useMemo(
    () => [
      { Header: "Č. Dokladu", accessor: "doklad", Footer: "CELKEM" },

      { Header: "Datum vystavení", accessor: "datum_vys",
        Cell: ({ value }) => formatApiDate(value), sortType: dateSort },

      { Header: "Datum UZP", accessor: "datum_uzp",
        Cell: ({ value }) => formatApiDate(value), sortType: dateSort },

      { Header: "Č. Rezervace", accessor: "id_rezervace" },

      { Header: "Platnost OD", accessor: "platnost_od",
        Cell: ({ value }) => formatApiDate(value), sortType: dateSort },

      { Header: "Platnost DO", accessor: "platnost_do",
        Cell: ({ value }) => formatApiDate(value), sortType: dateSort },

      {
        Header: "ZÁK. 0",
        accessor: "zak_0",
        Footer: (info) => {
          const total = React.useMemo(
            () => info.rows.reduce((sum, row) => row.values.zak_0 + sum, 0),
            [info.rows]
          );
          return <>{Math.round(total * 100) / 100}</>;
        },
      },
      {
        Header: "ZÁK. N.",
        accessor: "zak_n",
        Footer: (info) => {
          const total = React.useMemo(
            () => info.rows.reduce((sum, row) => row.values.zak_n + sum, 0),
            [info.rows]
          );
          return <>{Math.round(total * 100) / 100}</>;
        },
      },
      {
        Header: "DPH N.",
        accessor: "dph_n",
        Footer: (info) => {
          const total = React.useMemo(
            () => info.rows.reduce((sum, row) => row.values.dph_n + sum, 0),
            [info.rows]
          );
          return <>{Math.round(total * 100) / 100}</>;
        },
      },
      {
        Header: "ZÁK. V.",
        accessor: "zak_v",
        Footer: (info) => {
          const total = React.useMemo(
            () => info.rows.reduce((sum, row) => row.values.zak_v + sum, 0),
            [info.rows]
          );
          return <>{Math.round(total * 100) / 100}</>;
        },
      },
      {
        Header: "DPH V.",
        accessor: "dph_v",
        Footer: (info) => {
          const total = React.useMemo(
            () => info.rows.reduce((sum, row) => row.values.dph_v + sum, 0),
            [info.rows]
          );
          return <>{Math.round(total * 100) / 100}</>;
        },
      },
      {
        Header: "CELKEM KČ",
        accessor: "celkem",
        Footer: (info) => {
          const total = React.useMemo(
            () => info.rows.reduce((sum, row) => row.values.celkem + sum, 0),
            [info.rows]
          );
          return <>{Math.round(total * 100) / 100}</>;
        },
      },
      { Header: "Tel", accessor: "tel" },
      { Header: "Email", accessor: "email" },
    ],
    []
  );

  //React Table setup
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
    },
    useGlobalFilter,
    useSortBy
  );

  const { globalFilter } = state;

  //Debounce globálního filtru
  const debouncedSetGlobalFilter = React.useMemo(
    () => _.debounce((v) => setGlobalFilter(v), 350),
    [setGlobalFilter]
  );
  React.useEffect(() => () => debouncedSetGlobalFilter.cancel(), [debouncedSetGlobalFilter]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const dOd = toApiDateOnly(datumOd);
      const dDo = toApiDateOnly(datumDo);

      if (!dOd || !dDo) {
        alert("Špatně zadané datum!");
      } else {
        const requestOptions = { method: 'GET', headers: authHeader() };
        const stav = 2;

        const apiurl =
          process.env.REACT_APP_API_URL +
          "/api/private/prehledy/historie/" +
          user.u_fi + "/" + stav + "/" +
          "?datumOD=" + dOd + " 00:00:00&datumDO=" + dDo + " 23:59:00";

        const response = await fetch(apiurl, requestOptions);
        const apidata = await response.json();

        setData(apidata || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoading(false);
  }, [datumOd, datumDo, user?.u_fi]);

  const handleFormSubmit = useCallback((event) => {
    event.preventDefault();
    fetchData();
  }, [fetchData]);

  const handlePrintDoklad = useCallback((cisdok) => {
    const requestOptions = { method: 'POST', headers: authHeader() };
    return fetch(
      `${process.env.REACT_APP_API_URL}/api/private/printdoklad/${user.u_fi}/${cisdok}`,
      requestOptions
    ).then(() => {
      const pdfWindow = window.open();
      pdfWindow.location.href = 'https://e-kemp.eu/nodeserver/doklady/D' + cisdok + '.html';
    });
  }, [user?.u_fi]);

  const handleExcelClick = useCallback((e) => {
    e.preventDefault();

    const dOd = toApiDateOnly(datumOd);
    const dDo = toApiDateOnly(datumDo);

    if (!Array.isArray(data) || data.length <= 0) {
      alert("Špatně zvolená data!");
    } else {
      const exportData = data.map((row) => ({
        ...row,
        datum_vys: formatApiDate(row.datum_vys),
        datum_uzp: formatApiDate(row.datum_uzp),
        platnost_od: formatApiDate(row.platnost_od),
        platnost_do: formatApiDate(row.platnost_do),
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, "Doklady");
      XLSX.writeFile(wb, "doklady_" + (dOd || 'OD') + "_az_" + (dDo || 'DO') + ".xlsx");
    }
  }, [data, datumOd, datumDo]);

  /** ======= Render ======= */
  return (
    <>
      <div className="containerInputs">
        <h1 className="lblPrehled"> S2 - Přehled Dokladů</h1>

        <form className="formInputs" onSubmit={handleFormSubmit}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="cs">
            <DatePicker
              className="datumOD"
              label="Platnost OD"
              value={datumOd || null}
              onChange={(newValue) => setDatumOd(newValue)}
              required
              format="DD/MM/YYYY"
            />
            <DatePicker
              className="datumDOArch"
              label="Platnost DO"
              value={datumDo || null}
              minDate={datumOd || null}
              onChange={(newValue) => setDatumDo(newValue)}
              required
              format="DD/MM/YYYY"
            />
          </LocalizationProvider>

          <button className="btnInputSub" type="submit">Potvrdit</button>
          <button className="btnExcel" onClick={handleExcelClick}>
            <SiMicrosoftexcel />
          </button>
        </form>

        {/* Debounce pro globální filtr – předáváme debounced setter */}
        <GlobalFilter filter={globalFilter} setFilter={debouncedSetGlobalFilter} />
      </div>

      <div className="container">
        {
          loading ? (
            <div id="tablespinner" className="spinner-border"></div>
          ) : (
            <table {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                        {column.render("Header")}
                        <span>
                          {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                                               </span>
                      </th>
                    ))}
                    <th className="thoperace"><FaWrench className="iconWrench" /></th>
                  </tr>
                ))}
              </thead>

              <tbody {...getTableBodyProps()}>
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      ))}
                      <td className="colPrint">
                        <Tooltip title="Tisk.." arrow>
                          <a className="btnPrint" onClick={() => handlePrintDoklad(row.original.doklad)}>
                            <FaPrint />
                          </a>
                        </Tooltip>
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                {footerGroups.map((group) => (
                  <tr {...group.getFooterGroupProps()}>
                    {group.headers.map((column) => (
                      <td {...column.getFooterProps()}>{column.render("Footer")}</td>
                    ))}
                    <td></td>
                  </tr>
                ))}
              </tfoot>
            </table>
          )
        }
      </div>
    </>
  );
};

export default TableArchiv;
